import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Container,
    Button,
    Tooltip,
    IconButton,
    Avatar,
    TextField,
    Skeleton,
} from '@mui/material';

import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    ContentCopy as ContentCopyIcon,
    IosShare as IosShareIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { getBrandLogoProps, hasBrandLogo } from '../utils/brandLogoUtils';
import { useDebounce } from '../utils/useDebounce';
import { ContentTabs, ContentGridSkeleton } from '../components/ContentTabs';
import { Dropdown } from '../components/common/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import { misPodcastsMediaKitDetailPath } from '../miContenido/paths';
import { useTranslation } from 'react-i18next';


const STALE_TIME = 10 * 60 * 1000 // 10 minutes in milliseconds

// Skeleton components for progressive loading
const HeaderSkeleton = () => (
    <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="text" width={300} height={48} />
            </Box>
        </Box>
    </Box>
);

const FILTER_DROPDOWN_SX = {
    flex: '0 0 auto',
    minWidth: 160,
    width: { xs: 160, sm: 200, md: 220 },
} as const;

const FiltersSkeleton = () => (
    <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="text" width={150} height={40} />
            <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: 1 }} />
        </Box>
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 2,
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
            }}
        >
            <Skeleton
                variant="rectangular"
                sx={{ flex: '1 1 200px', minWidth: 180, height: 40, borderRadius: 1 }}
            />
            {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton
                    key={index}
                    variant="rectangular"
                    sx={{ ...FILTER_DROPDOWN_SX, height: 40, borderRadius: 1, flexShrink: 0 }}
                />
            ))}
        </Box>
    </Box>
);

const ContentTabsSkeleton = () => (
    <Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, pb: 1 }}>
                {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />
                ))}
            </Box>
        </Box>
        <ContentGridSkeleton tabKey="youtubeShorts" />
    </Box>
);

const MyPodcastDetail: React.FC = () => {
    const { t } = useTranslation("miContenido");
    const { slug } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isCompanyAdmin, user } = useAuth();

    // Brand filtering state
    const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
    const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Function to extract source ID from social media URLs
    const extractSourceId = (text: string): string => {
        // Instagram reel: https://www.instagram.com/reel/{sourceId}/ or /reels/{sourceId}/
        const instagramReelMatch = text.match(/instagram\.com\/reels?\/([A-Za-z0-9_-]+)/);
        if (instagramReelMatch) {
            return instagramReelMatch[1];
        }

        // Instagram post: https://www.instagram.com/p/{sourceId}/
        const instagramPostMatch = text.match(/instagram\.com\/p\/([A-Za-z0-9_-]+)/);
        if (instagramPostMatch) {
            return instagramPostMatch[1];
        }

        // TikTok: https://www.tiktok.com/@username/video/{sourceId}
        const tiktokMatch = text.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
        if (tiktokMatch) {
            return tiktokMatch[1];
        }

        // Facebook video: https://www.facebook.com/.../videos/{sourceId}
        const facebookVideoMatch = text.match(/facebook\.com\/[^?]*\/videos\/(\d+)/);
        if (facebookVideoMatch) {
            return facebookVideoMatch[1];
        }

        // Facebook reel: https://www.facebook.com/reel/{sourceId}
        const facebookReelMatch = text.match(/facebook\.com\/reel\/(\d+)/);
        if (facebookReelMatch) {
            return facebookReelMatch[1];
        }

        // Facebook post: https://www.facebook.com/.../posts/{sourceId}
        const facebookPostMatch = text.match(/facebook\.com\/[^?]*\/posts\/(\d+)/);
        if (facebookPostMatch) {
            return facebookPostMatch[1];
        }

        // Facebook watch short link: https://fb.watch/{sourceId}
        const fbWatchMatch = text.match(/fb\.watch\/([A-Za-z0-9_-]+)/);
        if (fbWatchMatch) {
            return fbWatchMatch[1];
        }

        // YouTube: https://www.youtube.com/watch?v={sourceId} or https://youtu.be/{sourceId}
        const youtubeMatch = text.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/);
        if (youtubeMatch) {
            return youtubeMatch[1];
        }

        // Return original text if no pattern matches
        return text;
    };


    // Fetch podcast details
    const { data: podcast, isLoading: podcastLoading, error: podcastError } = useQuery({
        queryKey: ['my-podcast-detail', slug],
        queryFn: async () => {
            // First get the podcast by slug
            const podcastData = await apiService.getPodcastBySlug(slug || '');
            return podcastData;
        },
        enabled: !!slug,
        staleTime: STALE_TIME, // 5 min
        gcTime: STALE_TIME, // 5 min
    });

    // Company podcast settings (livestream data on/off) for content detail dialog
    const companyId = user?.companyId ?? '';
    const { data: companyPodcastSettings } = useQuery({
        queryKey: ['company-podcast-settings', companyId, podcast?._id],
        queryFn: () => apiService.getCompanyPodcastSettings(companyId, podcast!._id),
        enabled: !!companyId && !!podcast?._id,
        staleTime: 2 * 60 * 1000, // 2 min
    });

    // Fetch social accounts for the podcast
    const { data: socialAccounts } = useQuery({
        queryKey: ['social-accounts', podcast?._id],
        queryFn: () => apiService.getSocialAccountsByPodcast(podcast!._id),
        enabled: !!podcast?._id,
        staleTime: STALE_TIME, // 5 minutes
        gcTime: STALE_TIME, // 5 minutes
    });


    // Fetch all metadata in one optimized request (tabs, months, brands, programs)
    const { data: metadata } = useQuery({
        queryKey: ['podcast-meta', podcast?._id, selectedMonth, selectedProgramId, companyId],
        queryFn: async () => {
            if (!podcast?._id) return null;
            return await apiService.getPodcastMeta(podcast._id, {
                monthFilter: selectedMonth || undefined,
                programId: selectedProgramId || undefined,
                companyId: companyId || undefined,
            });
        },
        enabled: !!podcast?._id,
        staleTime: STALE_TIME,
        gcTime: STALE_TIME,
    });

    // Extract data from metadata
    const programsData = useMemo(() => ({
        programs: metadata?.programs || []
    }), [metadata?.programs]);

    const brandsData = useMemo(() => ({
        brands: metadata?.brands || []
    }), [metadata?.brands]);

    const availableMonths = useMemo(() =>
        metadata?.availableMonths || [],
        [metadata?.availableMonths]);

    // Auto-select month if there's only one available
    useEffect(() => {
        if (availableMonths && availableMonths.length === 1 && !selectedMonth) {
            setSelectedMonth(availableMonths[0].value);
        }
    }, [availableMonths, selectedMonth]);

    // Program + month both filter private_content. Clearing month when the user
    // changes program avoids stale month ∩ program → empty grid (not on initial mount).
    const programFilterSyncRef = useRef(false);
    useEffect(() => {
        if (!programFilterSyncRef.current) {
            programFilterSyncRef.current = true;
            return;
        }
        setSelectedMonth(null);
    }, [selectedProgramId]);

    // If metadata refetch narrows months (e.g. after program change), drop invalid month.
    useEffect(() => {
        if (!selectedMonth || !availableMonths?.length) return;
        const stillValid = availableMonths.some((m) => m.value === selectedMonth);
        if (!stillValid) setSelectedMonth(null);
    }, [selectedMonth, availableMonths]);

    const handleBackToMyPodcasts = () => {
        navigate('/mis-podcasts');
    };

    const hasPrograms = !!(programsData?.programs && programsData.programs.length > 0);
    const hasMonths = !!(availableMonths && availableMonths.length > 0);

    if (podcastLoading) {
        return (
            <Container maxWidth="xl">
                <HeaderSkeleton />
                <Box sx={{ mb: 4 }}>
                    <FiltersSkeleton />
                    <ContentTabsSkeleton />
                </Box>
            </Container>
        );
    }

    if (podcastError) {
        return (
            <Container maxWidth="xl">
                <Alert severity="error" sx={{ mb: 3 }}>
                    {t("errors.loadPodcast")}
                </Alert>
            </Container>
        );
    }

    if (!podcast) {
        return (
            <Container maxWidth="xl">
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t("empty.podcastNotFound")}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            {/* Navigation Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ArrowBackIcon color='primary' onClick={handleBackToMyPodcasts} sx={{ cursor: 'pointer', fontSize: '2rem' }} />
                        <Typography
                            variant="h1"
                            component={RouterLink}
                            to="/mis-podcasts"
                            onClick={handleBackToMyPodcasts}
                        >
                            {podcast.title}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        component={RouterLink}
                        to={misPodcastsMediaKitDetailPath(String(podcast._id))}
                        startIcon={<IosShareIcon />}
                        sx={{ flexShrink: 0 }}
                    >
                        {t("actions.configureMediaKit")}
                    </Button>
                </Box>
            </Box>


            {/* Groups Section */}


            {/* Content Section */}
            <Box sx={{ mb: 4 }}>
                {/* Title Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h2" color="primary">
                        {t("sections.content")}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Create Collection Button - only for company admin users */}
                        {isCompanyAdmin && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(`/mis-podcasts/crear-coleccion?podcastId=${podcast?._id}`)}
                                sx={{ minWidth: 'auto' }}
                            >
                                {t("actions.createCollection")}
                            </Button>
                        )}

                        {/* Copy Podcast ID button - only in development */}
                        {process.env.NODE_ENV === 'development' && (
                            <Tooltip title={t("createCollection.copyPodcastId")}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => {
                                        if (podcast?._id) {
                                            navigator.clipboard.writeText(podcast._id);
                                            // You could add a toast notification here if needed
                                        }
                                    }}
                                >
                                    Copy ID
                                </Button>
                            </Tooltip>
                        )}

                        {/* Reload button - only in development */}
                        {process.env.NODE_ENV === 'development' && (
                            <Tooltip title={t("createCollection.reloadAllPlatforms")}>
                                <IconButton
                                    onClick={async () => {
                                        setIsRefreshing(true);
                                        try {
                                            // Trigger content collection from all social platforms
                                            console.log('🚀 Triggering content collection from all platforms...');

                                            const collectionPromises = [
                                                // Spotify collection
                                                apiService.triggerSpotifyCollection(podcast?._id || '', {
                                                    dryRun: false,
                                                    limit: 40,
                                                    force: false
                                                }).then(result => ({ platform: 'Spotify', result })),

                                                // YouTube collection
                                                apiService.triggerYouTubeCollection(podcast?._id || '', {
                                                    dryRun: false,
                                                    limit: 50, // 50 is the maximum limit for YouTube
                                                    force: true // Force to refresh insights on existing content
                                                }).then(result => ({ platform: 'YouTube', result })),

                                                // // TikTok collection
                                                apiService.triggerTikTokCollection(podcast?._id || '', {
                                                    dryRun: false,
                                                    limit: 50,
                                                    force: true // Force to refresh insights on existing content
                                                }).then(result => ({ platform: 'TikTok', result })),

                                                // Instagram collection
                                                apiService.triggerInstagramCollection(podcast?._id || '', {
                                                    dryRun: false,
                                                    limit: 50,
                                                    force: true // Force to refresh insights on existing content
                                                }).then(result => ({ platform: 'Instagram', result })),
                                            ];

                                            const results = await Promise.allSettled(collectionPromises);

                                            // Log results for each platform
                                            results.forEach((result, index) => {
                                                const platforms = ['Spotify', 'YouTube', 'TikTok', 'Instagram'];
                                                if (result.status === 'fulfilled') {
                                                    console.log(`✅ ${result.value.platform} collection result:`, result.value.result);
                                                } else {
                                                    console.error(`❌ ${platforms[index]} collection failed:`, result.reason);
                                                }
                                            });

                                            // Run content matching after collection
                                            if (podcast?._id) {
                                                try {
                                                    const matchResult = await apiService.triggerContentMatching(podcast._id);
                                                    console.log('✅ Content matching result:', matchResult);
                                                } catch (matchError) {
                                                    console.error('❌ Content matching failed:', matchError);
                                                }
                                            }

                                            // Refetch all relevant queries after collection
                                            await Promise.all([
                                                queryClient.invalidateQueries({ queryKey: ['my-podcast-detail'] }),
                                                queryClient.invalidateQueries({ queryKey: ['collections-content'] }),
                                                queryClient.invalidateQueries({ queryKey: ['podcast-brands'] }),
                                            ]);


                                        } catch (error) {
                                            console.error('❌ Error during content collection:', error);
                                        } finally {
                                            setIsRefreshing(false);
                                        }
                                    }}
                                    disabled={isRefreshing}
                                    size="small"
                                >
                                    {isRefreshing ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <RefreshIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {/* Search and Filters Row */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin',
                    }}
                >
                    <TextField
                        placeholder={t("filters.searchByTitleOrLink")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{ flex: '1 1 200px', minWidth: 180 }}
                    />

                    <Box sx={FILTER_DROPDOWN_SX}>
                        <Dropdown
                            label={t("filters.filterByBrand")}
                            value={selectedBrandId}
                            onChange={setSelectedBrandId}
                            emptyLabel={t("filters.allBrands")}
                            width="100%"
                            options={brandsData?.brands?.map((brand) => ({
                                value: brand._id,
                                label: brand.name,
                                avatar: hasBrandLogo(brand) ? getBrandLogoProps(brand).src : undefined,
                                logo: hasBrandLogo(brand) ? getBrandLogoProps(brand).src : undefined
                            })) || []}
                            renderOption={(option) => (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    overflow: 'hidden'
                                }}>
                                    <Avatar
                                        src={option.avatar || option.logo}
                                        alt={option.label}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 1,
                                            flexShrink: 0
                                        }}
                                    >
                                        {option.label.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1
                                    }}>
                                        {option.label}
                                    </Box>
                                </Box>
                            )}
                        />
                    </Box>

                    {hasPrograms && programsData?.programs && (
                        <Box sx={FILTER_DROPDOWN_SX}>
                            <Dropdown
                                label={t("filters.filterByProgram")}
                                value={selectedProgramId}
                                onChange={setSelectedProgramId}
                                emptyLabel={t("filters.allPrograms")}
                                width="100%"
                                options={programsData.programs.map((program: any) => ({
                                    value: String(program._id),
                                    label: program.name
                                }))}
                            />
                        </Box>
                    )}

                    {hasMonths && availableMonths && (
                        <Box sx={FILTER_DROPDOWN_SX}>
                            <Dropdown
                                label={t("filters.filterByMonth")}
                                value={selectedMonth}
                                onChange={setSelectedMonth}
                                emptyLabel={t("filters.allMonths")}
                                width="100%"
                                options={availableMonths}
                            />
                        </Box>
                    )}
                </Box>

                <ContentTabs
                    podcastId={podcast?._id}
                    programId={selectedProgramId}
                    brandId={selectedBrandId}
                    monthFilter={selectedMonth}
                    searchTerm={debouncedSearchTerm?.trim() ? extractSourceId(debouncedSearchTerm.trim()) : undefined}
                    socialAccounts={socialAccounts}
                    allBrands={brandsData?.brands}
                    selectedBrandId={selectedBrandId}
                    showLivestreamData={companyPodcastSettings?.livestreamDataEnabled ?? false}
                    companyId={companyId || undefined}
                />
            </Box>

        </Container>
    );
};

export default MyPodcastDetail; 